import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  useGetBlogsByCategoryQuery,
  useGetBlogCategoriesQuery,
} from '../redux/apiSlice';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertTriangle,
  BookOpen,
  Calendar,
  Tag,
  Home,
  User,
} from 'lucide-react';

interface Blog {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  alttext: string;
  featuredImage: string;
  delete: boolean;
  slug: string;
  tags: string[];
  publishedAt: string;
  status: number;
  seoTitle: string;
  seoDescription: string;
  author: {
    id: number;
    name: string;
  };
  categories: Array<{
    id: number;
    name: string;
    slug: string;
    postCount: number;
  }>;
}

const BlogCategory: React.FC = () => {
  const { categorySlug, pageNumber } = useParams<{
    categorySlug: string;
    pageNumber?: string;
  }>();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(
    pageNumber ? parseInt(pageNumber, 10) : 1,
  );
  const pageSize = 9;

  const { data, isLoading, error } = useGetBlogsByCategoryQuery({
    categorySlug: categorySlug || '',
    page: currentPage,
    pageSize,
  });
  const { data: categoriesData } = useGetBlogCategoriesQuery();

  // Use real categories from API or fallback to static ones
  const categories = categoriesData || [
    { id: 1, name: 'Programming', slug: 'programming', postCount: 12 },
    { id: 2, name: 'Web Development', slug: 'web-development', postCount: 8 },
    { id: 3, name: 'Mobile Apps', slug: 'mobile-apps', postCount: 5 },
    { id: 4, name: 'AI & Machine Learning', slug: 'ai-ml', postCount: 7 },
    { id: 5, name: 'Game Development', slug: 'game-dev', postCount: 4 },
    { id: 6, name: 'Tips & Tricks', slug: 'tips-tricks', postCount: 15 },
  ];

  // Get current category info
  const currentCategory = categories.find((cat) => cat.slug === categorySlug);

  // Update current page when URL parameter changes
  useEffect(() => {
    if (pageNumber) {
      const page = parseInt(pageNumber, 10);
      if (page !== currentPage && page > 0) {
        setCurrentPage(page);
      }
    }
  }, [pageNumber, currentPage]);

  // Set SEO meta tags
  useEffect(() => {
    if (currentCategory) {
      document.title = `${currentCategory.name} - Coding Resource Blog`;

      // Update meta description
      const metaDescription = document.querySelector(
        'meta[name="description"]',
      );
      const description = `Explore ${currentCategory.name} articles and tutorials on our coding resource blog.`;

      if (metaDescription) {
        metaDescription.setAttribute('content', description);
      } else {
        const newMetaDescription = document.createElement('meta');
        newMetaDescription.name = 'description';
        newMetaDescription.content = description;
        document.head.appendChild(newMetaDescription);
      }
    }

    return () => {
      document.title = 'MyCodeDragon';
    };
  }, [currentCategory]);

  // Function to get default image if featuredImage is not available
  const getImageSrc = (featuredImage: string) => {
    if (!featuredImage || featuredImage === '') {
      return '/assets/images/final_logo.png';
    }
    if (featuredImage.startsWith('http')) {
      return featuredImage;
    }
    return `${process.env.REACT_APP_API_URL}${featuredImage}`;
  };

  // Function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Function to get category names - categories are already objects in the blog data
  const getCategoryNames = (blogCategories: any[]) => {
    // If categories are already objects with name property, return them directly
    if (
      blogCategories &&
      blogCategories.length > 0 &&
      typeof blogCategories[0] === 'object'
    ) {
      return blogCategories.map((cat) => cat.name);
    }
    // Fallback: if categories are IDs, map them to names
    return categories
      .filter((cat) => blogCategories.includes(cat.id))
      .map((cat) => cat.name);
  };

  const handlePageChange = (page: number) => {
    if (page === 1) {
      navigate(`/blog/category/${categorySlug}`);
    } else {
      navigate(`/blog/category/${categorySlug}/page/${page}`);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPaginationPages = () => {
    if (!data || data.length === 0) return [];

    const totalPages = Math.ceil(data.length / pageSize);
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-16 w-16 animate-spin text-[#004aad] mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-600">
            Loading amazing blogs...
          </p>
        </div>
      </div>
    );
  }

  if (error || !currentCategory) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl shadow-lg p-8 max-w-md">
          <div className="text-red-500 mb-4">
            <AlertTriangle className="w-16 h-16 mx-auto" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            Category Not Found
          </h3>
          <p className="text-gray-600 mb-4">
            This category doesn't exist or is not available.
          </p>
          <Link
            to="/blog"
            className="bg-[#004aad] hover:bg-blue-800 text-white px-6 py-2 rounded-full font-medium transition-colors duration-200"
          >
            Back to All Blogs
          </Link>
        </div>
      </div>
    );
  }

  const blogs = data || [];
  const totalPages = blogs.length > 0 ? Math.ceil(blogs.length / pageSize) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50">
      {/* Breadcrumbs */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-blue-600 transition-colors">
              <Home className="w-4 h-4" />
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/blog" className="hover:text-blue-600 transition-colors">
              Blog
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-blue-600 font-medium">
              {currentCategory.name}
            </span>
          </nav>
        </div>
      </div>

      {/* Header Section */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                {currentCategory.name}
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-4">
              Discover amazing {currentCategory.name.toLowerCase()} blogs and
              tutorials!
            </p>
            {data && (
              <p className="text-sm text-gray-500">
                {data.length} article{data.length !== 1 ? 's' : ''} found
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {blogs.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-gray-400 mb-4">
              <BookOpen className="w-24 h-24 mx-auto" />
            </div>
            <h3 className="text-2xl font-bold text-gray-500 mb-2">
              No Articles Yet!
            </h3>
            <p className="text-gray-400 mb-6">
              Check back soon for exciting {currentCategory.name.toLowerCase()}{' '}
              content!
            </p>
            <Link
              to="/blog"
              className="bg-[#004aad] hover:bg-blue-800 text-white px-6 py-3 rounded-full font-semibold transition-colors duration-200"
            >
              Browse All Articles
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogs.map((blog: Blog) => (
                <Link
                  key={blog.id}
                  to={`/blog/${blog.slug}`}
                  className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
                >
                  {/* Blog Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={getImageSrc(blog.featuredImage)}
                      alt={blog.alttext || blog.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/assets/images/final_logo.png';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>

                  {/* Blog Content */}
                  <div className="p-6">
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatDate(blog.publishedAt)}
                      </div>
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-1" />
                        {blog.author.name}
                      </div>
                    </div>

                    <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-800 transition-colors duration-200 line-clamp-2">
                      {blog.title}
                    </h2>

                    <p className="text-gray-600 text-sm leading-relaxed line-clamp-3 mb-4">
                      {blog.excerpt}
                    </p>

                    {/* Categories */}
                    {blog.categories.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {getCategoryNames(blog.categories)
                          .slice(0, 2)
                          .map((category, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                            >
                              <Tag className="w-3 h-3 mr-1" />
                              {category}
                            </span>
                          ))}
                        {getCategoryNames(blog.categories).length > 2 && (
                          <span className="text-xs text-gray-500">
                            +{getCategoryNames(blog.categories).length - 2} more
                          </span>
                        )}
                      </div>
                    )}

                    {/* Read More Button */}
                    <div className="flex items-center text-[#004aad] font-medium text-sm group-hover:text-blue-800">
                      <span>Read More</span>
                      <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-200" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 mt-12">
                {/* Previous Button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`flex items-center px-4 py-2 rounded-full font-medium transition-all duration-200 ${
                    currentPage === 1
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-[#004aad] hover:bg-blue-100 hover:text-blue-800'
                  }`}
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex space-x-1">
                  {getPaginationPages().map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`w-10 h-10 rounded-full font-medium transition-all duration-200 ${
                        currentPage === page
                          ? 'bg-[#004aad] text-white shadow-lg'
                          : 'text-gray-600 hover:bg-blue-100 hover:text-blue-800'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                {/* Next Button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`flex items-center px-4 py-2 rounded-full font-medium transition-all duration-200 ${
                    currentPage === totalPages
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-[#004aad] hover:bg-blue-100 hover:text-blue-800'
                  }`}
                >
                  Next
                  <ChevronRight className="w-5 h-5 ml-1" />
                </button>
              </div>
            )}

            {/* Page Info */}
            {blogs.length > 0 && (
              <div className="text-center mt-6 text-gray-500 text-sm">
                Showing {blogs.length} articles in {currentCategory.name}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BlogCategory;
